import { Sparkles, X } from 'lucide-react'
import { lazy, Suspense, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Spinner } from '@/components/ds2'
import { useAppSelector } from '@/hooks'
import { selectApplication, selectFeatureFlags, selectUser } from '@/store/authSlice'
import { GENERAL_AGENT_PAGE_PATH } from '../constants'
import { useGeneralAgentChatStore } from '../hooks/generalAgentChatStore'
import { useLauncherPosition } from '../hooks/useLauncherPosition'

const ICON_SIZE = 44
const PANEL_GAP = 12
const PANEL_EDGE = 24

// The panel loads on the first open, not with every page.
const GeneralAgentPanel = lazy(() => import('./GeneralAgentPanel').then((m) => ({ default: m.GeneralAgentPanel })))

/**
 * A small icon at the bottom right of every admin page, kept clear of Intercom's bubble, that opens the general
 * agent's chat for a company its `general-agent` flag names. The conversation lives in the store, so closing,
 * expanding and minimizing keep it.
 */
export const GeneralAgentLauncher = () => {
    const enabled = useAppSelector(selectFeatureFlags).generalAgent ?? false
    const { pathname } = useLocation()
    const userId = useAppSelector(selectUser)?.id
    const projectName = useAppSelector(selectApplication)?.projectName
    const open = useGeneralAgentChatStore((s) => s.panelOpen)
    const setPanelOpen = useGeneralAgentChatStore((s) => s.setPanelOpen)
    const bindOwner = useGeneralAgentChatStore((s) => s.bindOwner)
    const onFullPage = pathname.startsWith(GENERAL_AGENT_PAGE_PATH)
    const showPanel = enabled && open && !onFullPage
    // Clear of Intercom's bubble; held still while the panel is open (Intercom's launcher is hidden then).
    const position = useLauncherPosition(ICON_SIZE, !enabled || onFullPage || showPanel)
    const panelBottom = position.bottom + ICON_SIZE + PANEL_GAP

    // The chat belongs to who is signed in, in which company: another user or company starts clean (logout is not a reload).
    useEffect(() => {
        if (userId && projectName) bindOwner(`${userId}:${projectName}`)
    }, [userId, projectName, bindOwner])

    if (!enabled || onFullPage) return null

    return (
        <>
            {showPanel && (
                <div
                    className="fixed z-[9990] w-[min(400px,calc(100vw-3rem))]"
                    style={{ right: PANEL_EDGE, bottom: panelBottom, height: `min(600px, calc(100dvh - ${panelBottom + PANEL_EDGE}px))` }}
                >
                    <Suspense
                        fallback={
                            <div className="flex h-full items-center justify-center rounded-2xl bg-surface shadow-overlay">
                                <Spinner size={20} />
                            </div>
                        }
                    >
                        <GeneralAgentPanel />
                    </Suspense>
                </div>
            )}
            <button
                type="button"
                onClick={() => setPanelOpen(!open)}
                aria-label={open ? 'Close the general agent' : 'Open the general agent'}
                aria-expanded={open}
                style={{ right: position.right, bottom: position.bottom }}
                className="fixed z-[9990] flex h-11 w-11 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-overlay transition-colors duration-150 hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
            >
                {open ? <X className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
            </button>
        </>
    )
}
